import { useTranslation } from 'react-i18next'

import { useFormikContext } from 'formik'

import { filterByKeyAndValue } from 'common/components/utils/SelectFilterUtils'

import {
  Col,
  DatePicker,
  DefaultInputV2,
  DefaultSelectV2,
  Icon,
  InputMask,
  Label,
  Radio,
  RedAsterisk,
  Row,
  Tooltip,
} from 'common/components'
import { disabledBirthDate, formatDate } from 'common/dateFormatters'
import { FormData } from 'common/hireEmployeeUtils'

interface EmployeePersonalDetailsFormProps {
  disableWorkEmailInput?: boolean
}

const EmployeePersonalDetailsForm = ({ disableWorkEmailInput = false }: EmployeePersonalDetailsFormProps) => {
  const { t: translate } = useTranslation()
  const { values, setFieldValue } = useFormikContext<FormData>()

  return (
    <Row>
      <Col sm={24} md={12} className="section-block_content-item">
        <DefaultInputV2
          showRequiredSign={true}
          name="firstName"
          label={translate('admin-dashboard.manage-people.edit-profile.first-name-label')}
          placeholder=""
        />
      </Col>
      <Col sm={24} md={12} className="section-block_content-item">
        <DefaultInputV2
          showRequiredSign={true}
          name="lastName"
          label={translate('admin-dashboard.manage-people.edit-profile.last-name-label')}
          placeholder=""
        />
      </Col>
      <Col sm={24} md={12} className="section-block_content-item">
        <DefaultInputV2
          showRequiredSign={true}
          name="workEmail"
          label={translate('admin-dashboard.manage-people.edit-profile.work-email-label')}
          disabled={disableWorkEmailInput}
        />
      </Col>
      <Col sm={24} md={12} className="section-block_content-item">
        <DefaultInputV2
          optional={true}
          name="personalEmail"
          label={translate('admin-dashboard.manage-people.edit-profile.personal-email-label')}
          affix={
            <Tooltip
              title={translate('admin-dashboard.manage-people.edit-profile.personal-email-tooltip')}
              position="right"
            >
              <Icon type="far fa-question-circle" className="info_icon ml-8" fontAwesome />
            </Tooltip>
          }
        />
      </Col>
      <Col sm={24} md={12} className="section-block_content-item">
        <DefaultSelectV2
          showRequiredSign={true}
          name="stateOfResidenceId"
          optional={false}
          placeholder={'Select state of residence'}
          label={translate('admin-dashboard.manage-people.edit-profile.state-of-residence')}
          filterOption={filterByKeyAndValue}
          showSearch
          items={values.availableStates}
        />
      </Col>
      <Col sm={24} md={12} className="section-block_content-item">
        <InputMask showRequiredSign={true} name="zipCode" label="Zip Code" placeholder="00000" mask="00000" />
      </Col>
      <Col sm={24} md={12} className="section-block_content-item">
        <DatePicker
          optional={!values.requireBirthDate}
          name="birthDay"
          placeholder={'MM/DD/YYYY'}
          id="birthDay"
          className="w-100"
          noStyles
          defaultValue={values.birthDay ? formatDate(values.birthDay) : undefined}
          hideError={true}
          showRequiredSign={values.requireBirthDate}
          label="Birth date"
          disabledDate={disabledBirthDate}
        />
      </Col>

      <Col sm={24} md={12} className="section-block_content-item">
        <DefaultInputV2
          optional={true}
          name="identificationNumber"
          label={translate('admin-dashboard.manage-people.edit-profile.ein-label')}
        />
      </Col>

      <Col span={24} className="section-block_content-item has-left-border">
        <Row>
          <Label>
            <RedAsterisk className={'mr-4'} />
            {translate('admin-dashboard.manage-people.edit-profile.sms-label')}
          </Label>
          <Radio.Group name="receiveSmsSelect" className="pr-8 pt-8" defaultValue={values?.receiveSms}>
            <Row>
              <Col span={4}>
                <Radio value={true} name={'receiveSms'} className="receive_sms_radio mr-8">
                  Yes
                </Radio>
              </Col>
              <Col span={4}>
                <Radio value={false} name={'receiveSms'} className="receive_sms_radio">
                  No
                </Radio>
              </Col>
            </Row>
          </Radio.Group>
        </Row>
      </Col>
      <Col sm={24} md={12} className="section-block_content-item">
        <Row className="pb-16">
          <Col span={24} className="left_border pl-24">
            <InputMask
              optional={!values.receiveSms}
              label={'Mobile phone number'}
              name="workPhone"
              mask="(000) 000-0000"
              placeholder="(555) 123-4567"
              onChange={(e) => {
                if (e.unmaskedValue === '') {
                  setFieldValue('workPhone', null)
                } else {
                  setFieldValue('workPhone', e.maskedValue)
                }
              }}
            />
          </Col>
        </Row>
      </Col>
    </Row>
  )
}

export default EmployeePersonalDetailsForm
